/**
 * WorkforceOps — the live day-of board: walk-in queues, today's appointments,
 * today's events and who's on the floor, in one glance, for ONE chosen
 * location. Read-only aggregation (actions live in Event Day / the queue
 * tools); refreshes every 60s.
 * Data: getOpsBoard (declared op), only ever for an explicitly chosen, served,
 * active location from listOpsBoardLocations (the "ops-board" catalog purpose).
 * Nothing here picks a location for the person.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Banner, Button, Dropdown, Icon, Pill, Skeleton, T, alpha } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { StatTile, FilterBar, EmptyState } from "../../shell/composites";
import { useIsMobile } from "../../shell/useResponsive";
import { useActiveLocationGate, type ServedLocation } from "../../shell/active-location";
import { friendlyTime } from "../../lib/datetime";
import { civilTodayInZone, isValidIanaZone } from "../floor/floor-calendar-data";
import { getOpsBoard, type OpsBoardSnapshot, type OpsAppointment, type OpsFloorEntry } from "./wf-api";
import { TinyProgress, hmLabel, hmRangeLabel } from "./wf-shared";
import { useT } from "../../lib/i18n";

const POLL_MS = 60_000;

const APPT_PILL: Record<OpsAppointment["status"], { tone: "success" | "warn" | "danger" | "info" | "neutral"; label: string; labelKey: string }> = {
  "upcoming": { tone: "neutral", label: "Upcoming", labelKey: "common.status.upcoming" },
  "checked-in": { tone: "info", label: "Checked in", labelKey: "workforce.ops.checkedIn" },
  "in-progress": { tone: "success", label: "In progress", labelKey: "workforce.ops.inProgress" },
  "done": { tone: "neutral", label: "Done", labelKey: "common.actions.done" },
  "no-show": { tone: "danger", label: "No-show", labelKey: "workforce.ops.noShow" },
};
const FLOOR_META: Record<OpsFloorEntry["status"], { color: string; label: string; labelKey: string }> = {
  "on-shift": { color: T.green1, label: "On shift", labelKey: "workforce.ops.onShift" },
  "break": { color: T.orange1, label: "On break", labelKey: "workforce.ops.onBreak" },
  "off-soon": { color: T.tDarkBlue, label: "Off soon", labelKey: "workforce.ops.offSoon" },
  "late": { color: T.red1, label: "Late", labelKey: "workforce.ops.late" },
};

/** Visually hidden, still announced — the skeleton itself is aria-hidden. */
const srOnly: CSSProperties = { position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0 };

/** A zone the board may compute "today" in, or null. Never the browser's. */
const zoneOf = (tz: unknown): string | null => (typeof tz === "string" && isValidIanaZone(tz) ? tz : null);

/** Provisional request date only, used when the location serves no valid zone.
 * It is never shown; the board's own zone decides which day is Today. */
const browserBootstrapToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

/** One option per served location, valued by id. Locations sharing a name
 * carry their id underneath so the two stay distinguishable. */
function locationOptions(locations: ServedLocation[]) {
  const seen = new Map<string, number>();
  for (const l of locations) seen.set(l.name, (seen.get(l.name) ?? 0) + 1);
  return locations.map((l) => ({ value: l.id, label: l.name, supportingText: (seen.get(l.name) ?? 0) > 1 ? l.id : undefined }));
}

type BoardRead = { key: string; board: OpsBoardSnapshot | null; failed: boolean; loading: boolean };

/**
 * The board for exactly one read scope (tenant + generation + location + civil
 * date, folded into `readKey`). A result is kept only while it belongs to the
 * scope on screen AND is the newest read started for it: an answer for a
 * previous location, tenant generation or date, or one overtaken by a later
 * read, is dropped. One read at a time per scope; refresh and poll both read
 * the scope current at the moment they fire. A failed refresh keeps the last
 * board it had for the same scope.
 */
function useOpsBoardRead(readKey: string | null, date: string | null, locationId: string | null) {
  const [read, setRead] = useState<BoardRead | null>(null);
  const target = useRef({ key: readKey, date, locationId });
  target.current = { key: readKey, date, locationId };
  const seq = useRef(0);
  const inFlight = useRef<string | null>(null);

  const start = useCallback(() => {
    const { key, date: day, locationId: id } = target.current;
    if (!key || !day || !id) return;
    if (inFlight.current === key) return;
    const mine = ++seq.current;
    inFlight.current = key;
    setRead((r) => (r && r.key === key ? { ...r, loading: true } : { key, board: null, failed: false, loading: true }));
    const finish = (board: OpsBoardSnapshot | null) => {
      if (mine !== seq.current || target.current.key !== key) return;
      inFlight.current = null;
      setRead((r) => {
        if (board) return { key, board, failed: false, loading: false };
        return { key, board: r && r.key === key ? r.board : null, failed: true, loading: false };
      });
    };
    let pending: Promise<OpsBoardSnapshot | null | undefined>;
    try { pending = Promise.resolve(getOpsBoard(day, id)); } catch (e) { pending = Promise.reject(e); }
    pending.then((b) => finish(b ?? null), () => finish(null));
  }, []);

  useEffect(() => {
    if (!readKey) return;
    start();
    const timer = setInterval(start, POLL_MS);
    return () => {
      clearInterval(timer);
      seq.current++;
      inFlight.current = null;
    };
  }, [readKey, start]);

  return { shown: read && read.key === readKey ? read : null, refresh: start };
}

function Panel({ title, count, children }: { title: string; count?: string; children: ReactNode }) {
  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: T.shadowSm, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{title}</span>
        {count ? <span style={{ fontSize: 11.5, color: T.soft, fontFamily: T.fontMono }}>{count}</span> : null}
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>{children}</div>
    </div>
  );
}

const kpiGrid = (isMobile: boolean): CSSProperties => ({ padding: "16px 24px 0", display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 12 });
const panelGrid = (isMobile: boolean): CSSProperties => ({ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, minmax(0, 1fr))", gap: 14, alignItems: "stretch" });

export default function WorkforceOps() {
  const t = useT();
  const isMobile = useIsMobile();
  /* Phone-only: the first two KPI tiles stand taller only below 640px. */
  const isPhone = useIsMobile(640);
  /* The ops board's own catalog (wfops.read tenant grant), never the settings
   * list: a role without locations.read still chooses its board location. */
  const locGate = useActiveLocationGate("ops-board");
  const { gate } = locGate;
  /* The one location this board reads: a served, active location someone chose.
   * Null in every other gate state, and then nothing is requested. */
  const location = gate.kind === "ready" ? gate.location : null;
  const locationId = location ? location.id : null;
  const scope = locationId && locGate.tenantKey ? JSON.stringify([locGate.tenantKey, locGate.generation, locationId]) : null;

  /* The query date is the location's civil Today. Start from the served
   * location's zone when it has a valid one; the board's echoed zone then
   * confirms it, and a different day re-reads before anything is shown. */
  const servedZone = location ? zoneOf(location.timezone) : null;
  const bootDate = useMemo(() => (scope ? (servedZone ? civilTodayInZone(new Date(), servedZone) : browserBootstrapToday()) : null), [scope, servedZone]);
  const [anchor, setAnchor] = useState<{ scope: string; date: string } | null>(null);
  const date = scope ? (anchor && anchor.scope === scope ? anchor.date : bootDate) : null;
  const readKey = scope && date ? JSON.stringify([scope, date]) : null;
  const { shown, refresh } = useOpsBoardRead(readKey, date, locationId);

  /* A board counts only if it echoes the chosen location, carries a valid zone,
   * and was read for that zone's Today. */
  const raw = shown?.board ?? null;
  const echoBad = !!raw && raw.locationId !== locationId;
  const boardZone = raw && !echoBad ? zoneOf(raw.tz) : null;
  const zoneBad = !!raw && !echoBad && !boardZone;
  const venueToday = boardZone ? civilTodayInZone(new Date(), boardZone) : null;
  const anchorPending = !!venueToday && venueToday !== date;
  useEffect(() => {
    if (scope && venueToday && anchorPending) setAnchor({ scope, date: venueToday });
  }, [scope, venueToday, anchorPending]);
  const data = raw && !echoBad && !zoneBad && !anchorPending ? raw : null;

  const busy = !!shown?.loading;
  const failedInitial = !!shown && shown.failed && !raw;
  const refreshFailed = !!shown && shown.failed && !!data;
  const catalogPending = gate.kind === "noTenant" || gate.kind === "loading";
  const showSkeleton = catalogPending || (!!readKey && !data && !failedInitial && !echoBad && !zoneBad);
  /* A read over something already on screen (a board, or a failure notice whose
   * Retry is now disabled) says it is working; under the skeleton it needn't. */
  const refreshing = busy && !showSkeleton;

  const locations = gate.kind === "ready" || gate.kind === "unselected" || gate.kind === "unavailable" ? gate.locations : null;
  const options = useMemo(() => locationOptions(locations ?? []), [locations]);
  const pickLocation = (id: string) => { locGate.select(id); };

  const retryLabel = t("common.actions.retry", "Retry");
  const refreshLabel = t("common.actions.refresh", "Refresh");
  const retryRead = <Button variant="secondary" size="sm" onClick={refresh} disabled={busy} aria-busy={busy || undefined}>{retryLabel}</Button>;

  const waiting = (data?.queues ?? []).reduce((s, q) => s + q.waiting, 0);
  const apptsLeft = (data?.appointments ?? []).filter((a) => a.status === "upcoming" || a.status === "checked-in").length;
  const onFloor = (data?.floor ?? []).filter((f) => f.status !== "late").length;
  const eventsLive = (data?.events ?? []).length;
  const quiet = !!data && data.queues.length === 0 && data.appointments.length === 0 && data.events.length === 0 && data.floor.length === 0;

  const board = data && (
    <div data-testid="wfops-board" style={panelGrid(isMobile)}>
      <Panel title={t("workforce.ops.walkInQueues", "Walk-in queues")} count={`${waiting} waiting`}>
        {data.queues.length === 0 ? <span style={{ fontSize: 12.5, color: T.soft, padding: 8 }}>{t("workforce.ops.noOpenQueuesToday", "No open queues today.")}</span> : data.queues.map((q) => {
          const short = q.staffed < q.needed;
          return (
            <div key={q.id} data-testid="wfops-queue-card" data-id={q.id} style={{ border: `1px solid ${short ? alpha(T.red1, 35) : T.border}`, borderRadius: 8, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.n1, flex: 1 }}>{q.name}</span>
                <span style={{ fontSize: 12, fontFamily: T.fontMono, color: short ? T.red1 : T.soft }}>{q.staffed}/{q.needed} {t("workforce.ops.staffed", "staffed")}</span>
              </div>
              <div style={{ display: "flex", gap: 14, fontSize: 11.5, color: T.soft }}>
                <span><b style={{ color: T.n1, fontFamily: T.fontMono }}>{q.waiting}</b> {t("workforce.ops.waiting", "waiting")}</span>
                <span>{t("workforce.ops.avg", "avg")}{" "}<b style={{ color: T.n1, fontFamily: T.fontMono }}>{q.avgWaitMins}{t("workforce.ops.m", "m")}</b></span>
                <span>{t("workforce.ops.longest", "longest")}{" "}<b style={{ color: q.longestWaitMins > 20 ? T.red1 : T.n1, fontFamily: T.fontMono }}>{q.longestWaitMins}{t("workforce.ops.m", "m")}</b></span>
              </div>
              {q.entries.slice(0, 4).map((e) => (
                <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: e.status === "serving" ? T.green1 : e.status === "called" ? T.orange1 : T.n4, flexShrink: 0 }} />
                  <span style={{ color: T.n1, fontWeight: 600, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.name}</span>
                  {e.service ? <span style={{ color: T.soft, whiteSpace: "nowrap" }}>{e.service}</span> : null}
                  <span style={{ color: T.soft, fontFamily: T.fontMono }}>{friendlyTime(e.joinedAt, data.tz)}</span>
                </div>
              ))}
              {q.entries.length > 4 ? <span style={{ fontSize: 11, color: T.soft }}>+{q.entries.length - 4} {t("workforce.ops.moreInLine", "more in line")}</span> : null}
            </div>
          );
        })}
      </Panel>

      <Panel title={t("workforce.ops.appointmentsToday", "Appointments today")} count={`${apptsLeft} to go`}>
        {data.appointments.length === 0 ? <span style={{ fontSize: 12.5, color: T.soft, padding: 8 }}>{t("workforce.ops.noAppointmentsBookedToday", "No appointments booked today.")}</span> : data.appointments.map((a) => (
          <div key={a.id} data-testid="wfops-appt-row" data-id={a.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, border: `1px solid ${T.n6}` }}>
            <span style={{ fontFamily: T.fontMono, fontSize: 12, color: T.n1, width: 62, flexShrink: 0 }}>{hmLabel(a.start)}</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.customer}</span>
              <span style={{ display: "block", fontSize: 11.5, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.service} · {a.staffName}</span>
            </span>
            <Pill tone={APPT_PILL[a.status].tone}>{t(APPT_PILL[a.status].labelKey, APPT_PILL[a.status].label)}</Pill>
          </div>
        ))}
      </Panel>

      <Panel title={t("workforce.ops.eventsToday", "Events today")} count={`${eventsLive}`}>
        {data.events.length === 0 ? <span style={{ fontSize: 12.5, color: T.soft, padding: 8 }}>{t("workforce.ops.noEventsRunningToday", "No events running today.")}</span> : data.events.map((e) => {
          const short = e.staffOnShift < e.staffNeeded;
          return (
            <div key={e.id} data-testid="wfops-event-card" data-id={e.id} style={{ border: `1px solid ${short ? alpha(T.red1, 35) : T.border}`, borderRadius: 8, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{e.title}</span>
              <span style={{ fontSize: 11.5, color: T.soft }}>{hmRangeLabel(e.start, e.end)}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: T.soft }}>
                <span style={{ width: 78 }}>{t("workforce.ops.checkIns", "Check-ins")}</span><TinyProgress num={e.checkedIn} den={Math.max(1, e.registered)} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: short ? T.red1 : T.soft }}>
                <span style={{ width: 78 }}>{t("workforce.ops.staff", "Staff")}</span><TinyProgress num={e.staffOnShift} den={Math.max(1, e.staffNeeded)} />
              </div>
            </div>
          );
        })}
      </Panel>

      <Panel title={t("workforce.ops.onTheFloor", "On the floor")} count={`${onFloor} on shift`}>
        {data.floor.length === 0 ? <span style={{ fontSize: 12.5, color: T.soft, padding: 8 }}>{t("workforce.ops.nobodyScheduledRightNow", "Nobody scheduled right now.")}</span> : data.floor.map((f) => {
          const meta = FLOOR_META[f.status];
          return (
            <div key={f.staffId} data-testid="wfops-floor-row" data-id={f.staffId} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: meta.color, flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.name}</span>
                <span style={{ display: "block", fontSize: 11.5, color: T.soft }}>{f.role}</span>
              </span>
              <span style={{ fontSize: 11.5, color: T.soft, whiteSpace: "nowrap" }}>{t(meta.labelKey, meta.label)} {t("workforce.ops.until", "· until")}{" "}{hmLabel(f.until)}</span>
            </div>
          );
        })}
      </Panel>
    </div>
  );

  /* Shaped like what lands next at every width: four KPI tiles and four panels,
   * on the same grids the board uses (tiles two-up and panels in one column on
   * a phone). */
  const loadingLabel = t("workforce.ops.loadingTheBoard", "Loading the board…");
  const kpiSkeleton = (
    <div data-testid="wfops-kpi-skeleton" aria-hidden="true" style={kpiGrid(isMobile)}>
      {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} height={isPhone && i < 2 ? 120 : 112} />)}
    </div>
  );
  const boardSkeleton = (
    <div data-testid="wfops-skeleton" role="status" aria-busy="true">
      <span style={srOnly}>{loadingLabel}</span>
      <div aria-hidden="true" style={panelGrid(isMobile)}>
        {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} height={isMobile ? 220 : 360} />)}
      </div>
    </div>
  );

  let notice: ReactNode = null;
  if (gate.kind === "denied") {
    notice = (
      <Banner tone="danger" testid="wfops-location-denied" title={t("workforce.ops.locationsDeniedTitle", "You can't view this workspace's locations")}>
        {t("workforce.ops.locationsDeniedBody", "The ops board needs access to locations. Ask a workspace admin for it.")}
      </Banner>
    );
  } else if (gate.kind === "error") {
    notice = (
      <Banner tone="danger" testid="wfops-location-error" title={t("workforce.ops.locationsFailedTitle", "Locations couldn't be loaded")}
        action={<Button variant="secondary" size="sm" onClick={locGate.retry}>{retryLabel}</Button>}>
        {t("workforce.ops.locationsFailedBody", "The board can't be shown until this workspace's locations load. Try again in a moment.")}
      </Banner>
    );
  } else if (gate.kind === "unavailable") {
    notice = (
      <Banner tone="warning" testid="wfops-location-unavailable" title={t("workforce.ops.locationUnavailableTitle", "Your saved location isn't available")}>
        {t("workforce.ops.locationUnavailableBody", "It's inactive or no longer part of this workspace. Choose a location to see its board.")}
      </Banner>
    );
  } else if (failedInitial) {
    notice = (
      <Banner tone="danger" testid="wfops-load-failed" title={t("workforce.ops.loadFailedTitle", "The ops board couldn't be loaded")} action={retryRead}>
        {t("workforce.ops.loadFailedBody", "Your location is still selected. Try again in a moment.")}
      </Banner>
    );
  } else if (echoBad) {
    notice = (
      <Banner tone="danger" testid="wfops-location-echo-mismatch" title={t("workforce.ops.locationEchoTitle", "This board isn't for the selected location")} action={retryRead}>
        {t("workforce.ops.locationEchoBody", "The board didn't confirm the selected location, so it isn't shown. Retry, or choose the location again.")}
      </Banner>
    );
  } else if (zoneBad) {
    notice = (
      <Banner tone="danger" testid="wfops-timezone-invalid" title={t("workforce.ops.timezoneInvalidTitle", "This location has no valid time zone")} action={retryRead}>
        {t("workforce.ops.timezoneInvalidBody", "Today can't be worked out for this location, so the board isn't shown. Retry once the location's time zone is configured.")}
      </Banner>
    );
  } else if (refreshFailed && data) {
    notice = (
      <Banner tone="warning" testid="wfops-refresh-failed" title={t("workforce.ops.refreshFailedTitle", "Couldn't refresh the board")} action={retryRead}>
        {t("workforce.ops.refreshFailedBody", "Showing the board as of")}{" "}{friendlyTime(data.asOf, data.tz)}.
      </Banner>
    );
  }

  let content: ReactNode = null;
  if (gate.kind === "empty") {
    content = <EmptyState icon={<Icon.clock size={20} />} title={t("workforce.ops.noLocationsTitle", "No locations set up")} body={t("workforce.ops.noLocationsBody", "This workspace has no active locations yet. Add one in Locations to use the ops board.")} />;
  } else if (gate.kind === "unselected") {
    content = <EmptyState icon={<Icon.clock size={20} />} title={t("workforce.ops.chooseLocationTitle", "Choose a location")} body={t("workforce.ops.chooseLocationBody", "The ops board shows one location's day. Choose the location above to see it.")} />;
  } else if (showSkeleton) {
    content = boardSkeleton;
  } else if (quiet) {
    content = <EmptyState icon={<Icon.clock size={20} />} title={t("workforce.ops.quietDay", "Quiet day")} body={t("workforce.ops.noQueuesAppointmentsEventsOrShiftsAtThisLocationToday", "No queues, appointments, events or shifts at this location today.")} />;
  } else if (data) {
    content = board;
  }

  return (
    <Screen rail="wf-ops">
      <PageHeader title={t("workforce.ops.opsBoard", "Ops board")} subtitle={t("workforce.ops.liveViewOfTodayAcrossQueuesAppointmentsEventsAndTheFloorAuto", "Live view of today across queues, appointments, events and the floor. Auto-refreshes every minute.")} breadcrumb={null}
        actions={<>
          {data ? <span style={{ fontSize: 12, color: T.soft }}>{t("workforce.ops.asOf", "As of")}{" "}{friendlyTime(data.asOf, data.tz)}</span> : null}
          <Button testid="wfops-refresh-action" variant="secondary" iconOnly={<Icon.refresh size={15} />} onClick={refresh}
            disabled={!readKey || busy} aria-busy={busy || undefined} aria-label={refreshLabel} title={refreshLabel} />
        </>} />
      <DataSourceStrip screen="wfops" ops={["getOpsBoard"]} />
      {data ? (
        <div data-testid="wfops-kpis" style={kpiGrid(isMobile)}>
          <StatTile label={t("workforce.ops.waitingInQueues", "Waiting in queues")} value={waiting} icon={<Icon.list size={14} />} accent={waiting > 0 ? T.orange1 : T.green1} />
          <StatTile label={t("workforce.ops.appointmentsLeft", "Appointments left")} value={apptsLeft} icon={<Icon.clock size={14} />} accent={T.tDarkBlue} />
          <StatTile label={t("workforce.ops.eventsToday", "Events today")} value={eventsLive} icon={<Icon.ticket size={14} />} accent={T.tPurple} />
          <StatTile label={t("workforce.ops.onTheFloor", "On the floor")} value={onFloor} icon={<Icon.users size={14} />} accent={T.p1} />
        </div>
      ) : showSkeleton ? kpiSkeleton : null}
      <FilterBar>
        {locations ? (
          <Dropdown testid="wfops-location-picker" options={options} value={locationId} onChange={pickLocation}
            aria-label={t("workforce.ops.location", "Location")} placeholder={t("workforce.ops.chooseLocation", "Choose a location")}
            size="sm" portal style={{ flex: "1 1 220px", minWidth: 0, maxWidth: 360 }} />
        ) : null}
        <span style={{ marginLeft: "auto", fontSize: 12, color: T.soft }}>{t("workforce.ops.today", "Today")}</span>
      </FilterBar>
      <div style={{ flex: 1, overflow: "auto", padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
        {notice}
        {refreshing ? <div role="status" aria-live="polite" data-testid="wfops-refreshing" style={{ fontSize: 12.5, color: T.soft }}>{t("workforce.ops.refreshing", "Refreshing the board…")}</div> : null}
        {content}
      </div>
    </Screen>
  );
}
